/**
 * Reading a spreadsheet the administrator dropped onto the page.
 *
 * The whole of file import happens here, in the browser: the file is read into
 * pairs and only the pairs are ever posted. The server has no upload route, no
 * temporary directory and no multipart parser, which is the cheapest possible
 * way to have none of the problems those bring (PLAN §9 Mode C).
 *
 * Separate from `app.ts` because nothing in here touches the DOM, so it can be
 * tested directly rather than through a browser.
 */

import { parseCsvPairs } from '../domain/csv.ts';
import type { ParsedPair } from '../domain/recipients.ts';
import type { TableProblem } from '../domain/table.ts';
import { parseXlsxPairs } from '../domain/xlsx.ts';

export type ImportSource = 'csv' | 'xlsx';

export interface SpreadsheetImport {
  readonly source: ImportSource;
  readonly pairs: readonly ParsedPair[];
  readonly hasHeader: boolean;
  readonly rows: number;
  /** Which columns were read, so the preview can say so and be corrected. */
  readonly phoneColumn: number;
  readonly codeColumn: number;
  /** XLSX only. */
  readonly sheetName?: string;
  /** CSV only. */
  readonly delimiter?: string;
  readonly problem?: TableProblem;
}

/** `PK` — the first four bytes of every ZIP, and so of every .xlsx. */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

/** U+FFFD, what a decoder leaves behind where the bytes did not fit the encoding. */
const REPLACEMENT = '�';

export async function readSpreadsheet(file: Blob, name = ''): Promise<SpreadsheetImport> {
  const bytes = new Uint8Array(await file.arrayBuffer());

  // The bytes decide, not the extension: a workbook saved as `list.csv` is
  // still a workbook, and telling the administrator their file is corrupt when
  // it opens perfectly well in Excel would be a lie.
  if (isZip(bytes) || name.toLowerCase().endsWith('.xlsx')) {
    const outcome = await parseXlsxPairs(bytes);
    return { source: 'xlsx', ...outcome };
  }

  const outcome = parseCsvPairs(decode(bytes));
  return { source: 'csv', ...outcome };
}

function isZip(bytes: Uint8Array): boolean {
  return ZIP_MAGIC.every((byte, index) => bytes[index] === byte);
}

/**
 * UTF-8 unless the bytes say otherwise. A CSV exported from Excel on a Russian
 * Windows is usually Windows-1251, where a UTF-8 decode turns every Cyrillic
 * promo code into replacement characters — visible, but only after the import.
 */
function decode(bytes: Uint8Array): string {
  const utf8 = new TextDecoder('utf-8').decode(bytes);
  if (!utf8.includes(REPLACEMENT)) return utf8;

  try {
    return new TextDecoder('windows-1251').decode(bytes);
  } catch {
    // A browser without that decoder: the replacement characters are the honest
    // result, and they are at least visible in the preview.
    return utf8;
  }
}
